import { parseResponse } from '$lib/client/responseHelpers';

import type { ApiTypes } from '@api/client';
import type { Config } from '@api/types';
import { getConfig } from '@api/user/config';
import { Cookie } from '@shared/cookies';
import { ExperimentGroup, ExperimentType } from '@shared/experiment';
import { Billing, Feature } from '@shared/types';
import { StreamProvider } from '@shared/types/room';
import { getCookie } from '@utils/cookies';

const defaultValue: Config = {
    version: '0.0.0',
    billingName: Billing.CentroBill,
    isBot: false,
    contest: null,
    experiments: [],
    isDesktopDevice: false,
    isUnsupportedDevice: false,
    isVpnDetected: false,
    recaptchaParam: {
        isCommon: false,
        siteKey: '',
        siteKeyV2: '',
    },
    googleClientId: '',
    welcomeTimePackage: {
        welcomeSeconds: 0,
        afterSignupSeconds: 0,
    },
    allowedFeatures: [],
    debug: false,
    gtmId: '',
    duplicateUsername: undefined,
    statsId: '',
};

class ConfigService {
    private value: Config;

    constructor() {
        this.value = defaultValue;
    }

    public get<T extends keyof Config>(key: T): Config[T] {
        return this.value[key];
    }

    public getExperimentGroup(expType: ExperimentType): ExperimentGroup | null {
        const existExp = this.value.experiments
            .find(e => e.type === expType);
        if (existExp) {
            return existExp.g as ExperimentGroup;
        }
        return null;
    }

    public init(initialValue: Config): void {
        this.value = { ...defaultValue, ...initialValue };
    }

    public update(value: Partial<Config>): void {
        this.value = { ...this.value, ...value };
    }

    public async reload(): Promise<void> {
        const data = parseResponse(await getConfig());
        if (data) {
            this.init(data);
        }
    }

    public isFeatureAllowed(feature: Feature): boolean {
        return this.value.allowedFeatures.includes(feature);
    }

    public isPremiumAllowed(user?: ApiTypes.UserCurrent): boolean {
        return (
            this.value.allowedFeatures.includes(Feature.BuyMembershipPremiumDialog) &&
            Boolean(user?.isPremiumAllowed) &&
            !this.get('isVpnDetected')
        );
    }

    public getStreamProvider(): StreamProvider {
        const isStripChat = getCookie(Cookie.IsStripChat);
        if (isStripChat) {
            return StreamProvider.StripChat;
        }
        const configFromCookie =
            getCookie(Cookie.QaTest) === 'true' && getCookie(Cookie.StreamingProvider);

        if (configFromCookie && Object.values(StreamProvider).includes(configFromCookie as StreamProvider)) {
            return configFromCookie as StreamProvider;
        }

        const exp = this.value.experiments.find((e) => e.type === ExperimentType.FF008);
        switch (exp?.g) {
            case ExperimentGroup.B:
                return StreamProvider.IvsRealTime;
            default:
                return StreamProvider.CloudFlare;
        }
    }
}

export const configService = new ConfigService();
